/**
 * Platform administrator management for super_admin users.
 * Lists and manages users with platform-wide system access, separate from
 * account-level user management in settings/UserManagement.tsx.
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";

interface User {
	id: string;
	email: string;
	name: string | null;
	createdAt: Date;
	updatedAt: Date;
}

/**
 * Platform Administrators management tab
 * Lists and manages super_admin users only
 */
export function UsersTab() {
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [editingUser, setEditingUser] = useState<User | null>(null);
	const [deletingUser, setDeletingUser] = useState<User | null>(null);

	// Form state for create dialog
	const [newEmail, setNewEmail] = useState("");
	const [newName, setNewName] = useState("");

	// Form state for edit dialog
	const [editName, setEditName] = useState("");

	const utils = trpc.useUtils();

	const usersQuery = trpc.users.list.useQuery();

	const createMutation = trpc.users.create.useMutation({
		onSuccess: () => {
			void utils.users.list.invalidate();
			setCreateDialogOpen(false);
			resetCreateForm();
		},
	});

	const updateMutation = trpc.users.update.useMutation({
		onSuccess: () => {
			void utils.users.list.invalidate();
			setEditingUser(null);
		},
	});

	const deleteMutation = trpc.users.delete.useMutation({
		onSuccess: () => {
			void utils.users.list.invalidate();
			setDeletingUser(null);
		},
	});

	/**
	 * Resets the create form to initial state
	 */
	const resetCreateForm = () => {
		setNewEmail("");
		setNewName("");
	};

	/**
	 * Creates a new administrator
	 */
	const handleCreate = () => {
		createMutation.mutate({
			email: newEmail,
			name: newName || undefined,
		});
	};

	/**
	 * Opens the edit dialog for a user
	 * @param user - the user to edit
	 */
	const handleEdit = (user: User) => {
		setEditingUser(user);
		setEditName(user.name ?? "");
	};

	/**
	 * Updates the currently editing user
	 */
	const handleUpdate = () => {
		if (!editingUser) return;
		updateMutation.mutate({
			id: editingUser.id,
			name: editName || undefined,
		});
	};

	/**
	 * Deletes the user marked for deletion
	 */
	const handleDelete = () => {
		if (!deletingUser) return;
		deleteMutation.mutate({ id: deletingUser.id });
	};

	/**
	 * Formats a date for display
	 * @param date - the date to format
	 */
	const formatDate = (date: Date) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">Platform Administrators</h2>
				<Button onClick={() => { setCreateDialogOpen(true); }}>Add Administrator</Button>
			</div>

			<div className="rounded-lg border">
				{usersQuery.isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading administrators...</div>
				) : usersQuery.error ? (
					<div className="p-8 text-center text-destructive">
						Error loading administrators: {usersQuery.error.message}
					</div>
				) : usersQuery.data?.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">No administrators found.</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Email</TableHead>
								<TableHead>Name</TableHead>
								<TableHead>Created</TableHead>
								<TableHead className="w-[100px]">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{usersQuery.data?.map((user) => (
								<TableRow key={user.id}>
									<TableCell className="font-medium">{user.email}</TableCell>
									<TableCell>{user.name ?? "-"}</TableCell>
									<TableCell>{formatDate(user.createdAt)}</TableCell>
									<TableCell>
										<div className="flex gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => { handleEdit(user); }}
											>
												Edit
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-destructive hover:text-destructive"
												onClick={() => { setDeletingUser(user); }}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Create User Dialog */}
			<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Platform Administrator</DialogTitle>
						<DialogDescription>
							Create a new platform administrator. They will have full system access.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="email">Email</Label>
								<Input
									id="email"
									type="email"
									placeholder="user@example.com"
									value={newEmail}
									onChange={(e) => { setNewEmail(e.target.value); }}
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="name">Name (optional)</Label>
								<Input
									id="name"
									placeholder="John Doe"
									value={newName}
									onChange={(e) => { setNewName(e.target.value); }}
								/>
							</div>
						</div>
						{createMutation.error && (
							<p className="text-sm text-destructive">{createMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								type="button"
								onClick={() => {
									setCreateDialogOpen(false);
									resetCreateForm();
								}}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={!newEmail || createMutation.isPending}
							>
								{createMutation.isPending ? "Creating..." : "Create Administrator"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Edit User Dialog */}
			<Dialog open={editingUser !== null} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Platform Administrator</DialogTitle>
						<DialogDescription>
							Update administrator details. Email cannot be changed.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label>Email</Label>
								<Input value={editingUser?.email ?? ""} disabled />
							</div>
							<div className="grid gap-2">
								<Label htmlFor="edit-name">Name</Label>
								<Input
									id="edit-name"
									placeholder="John Doe"
									value={editName}
									onChange={(e) => { setEditName(e.target.value); }}
								/>
							</div>
						</div>
						{updateMutation.error && (
							<p className="text-sm text-destructive">{updateMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button variant="outline" type="button" onClick={() => { setEditingUser(null); }}>
								Cancel
							</Button>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deletingUser !== null} onOpenChange={(open) => { if (!open) setDeletingUser(null); }}>
				<DialogContent>
					<form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
						<DialogHeader>
							<DialogTitle>Delete Administrator</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete {deletingUser?.email}? This action cannot be
								undone.
							</DialogDescription>
						</DialogHeader>
						{deleteMutation.error && (
							<p className="text-sm text-destructive">{deleteMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => { setDeletingUser(null); }}>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="destructive"
								disabled={deleteMutation.isPending}
							>
								{deleteMutation.isPending ? "Deleting..." : "Delete Administrator"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
